import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useKeycloak } from '@react-keycloak/web';

interface BulkStatsData {
  series: number[];
}

export function useBulkStats() {
  const queryClient = useQueryClient();
  const { keycloak } = useKeycloak();

  return useQuery({
    queryKey: ['bulkStats'],
    queryFn: async (): Promise<BulkStatsData> => {

      const response = await fetch(
        `${import.meta.env.VITE_IMPORT_ENGINE_ENDPOINT}/api/v1/bundles/query_bundle/functions/bulk_job_stats`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${keycloak.token}`
          },
          method: 'GET',
        }
      );

      let data = await response.json();

      const { size, high, medium, default: normal, low } = data?.data;
      const existingData = queryClient.getQueryData<BulkStatsData>(['bulkStats']);
      
      // Mapping of series to their corresponding values and names
      const seriesMap = {
        lowSeries: { name: 'Low Priority', value: low },
        defaultSeries: { name: 'Normal Priority', value: normal },
        mediumSeries: { name: 'Medium Priority', value: medium },
        highSeries: { name: 'High Priority', value: high },
      };
      
      let updatedSeries = [];
      
      // Check if existing data exists and is in the correct format
      if (existingData && Array.isArray(existingData.series)) {
        // Loop through the seriesMap to process each series
        Object.keys(seriesMap).forEach(key => {
          const { name, value } = seriesMap[key];
      
          const seriesItem = existingData.series.find(item => item.name === name);
      
          if (seriesItem) {
            // Copy the existing 'data' array and shift/push the new value
            const updatedData = [...seriesItem.data];
            updatedData.shift();  // Remove the first value
            updatedData.push(value);  // Add the new value to the end
      
            // Push the updated series with the new data
            updatedSeries.push({
              ...seriesItem,
              data: updatedData,  // Update only the 'data' field
            });
          } else {
            // If no existing series with the name, initialize it with nulls and the new value
            updatedSeries.push({
              type: 'line',
              name: name,
              data: [null, null, null, null, null, value],  // Initialize with nulls and the new value at the end
              // areaStyle: {},
              stack: 'Total',
            });
          }
        });
      } else {
        // If no existing data is found (initial request), create the series with nulls
        Object.keys(seriesMap).forEach(key => {
          const { name, value } = seriesMap[key];
          updatedSeries.push({
            type: 'line',
            name: name,
            data: [null, null, null, null, null, value],  // Initialize with nulls and the new value at the end
            // areaStyle: {},
            stack: 'Total',
          });
        });
      }
      
      // Return the updated series
      return {
        series: updatedSeries,
      };
      
      
      
    },
    refetchInterval: 6 * 1000
  });
}
